/**
 * Unified console output system for all logs (frontend and backend).
 * This is the SINGLE point where all logs go to the actual browser console.
 * Handles filtering, formatting, and output for both local and streamed logs.
 */

export interface LogFilters {
  level: string;
  categories: string;
  streamingEnabled: boolean;
}

class UnifiedConsole {
  private filters: LogFilters = {
    level: 'info',
    categories: '',
    streamingEnabled: false
  };

  /**
   * Update the active filters.
   */
  setFilters(filters: Partial<LogFilters>): void {
    this.filters = { ...this.filters, ...filters };
  }

  /**
   * Get current filters.
   */
  getFilters(): LogFilters {
    return { ...this.filters };
  }

  /**
   * Output a log to the console if it passes filters.
   * This is the ONLY place that should call console.* methods.
   *
   * @param tier - 'FE' for frontend, 'Backend' for backend, or specific tier like 'API'
   * @param category - Log category (e.g., 'API', 'UI', 'Auth')
   * @param level - Log level ('debug', 'info', 'warn', 'error')
   * @param message - Primary log message
   * @param args - Additional arguments to log
   */
  output(
    tier: string,
    category: string,
    level: string,
    message: string,
    ...args: any[]
  ): void {
    // Check level filter
    if (!this.passesLevelFilter(level)) {
      return; // Filtered out by level
    }

    // Check category filter
    if (!this.passesCategoryFilter(category)) {
      return; // Filtered out by category
    }

    // Format the message with appropriate prefixes
    let prefix: string;

    if (tier === 'FE' && this.filters.streamingEnabled) {
      // Frontend with streaming enabled: [FE][Category]
      prefix = `[FE][${category}]:`;
    } else if (tier === 'Backend') {
      // Backend logs: [Backend][Category]
      prefix = `[Backend][${category}]:`;
    } else if (tier !== 'FE') {
      // Other tiers (like from backend): [Tier][Category]
      prefix = `[${tier}][${category}]:`;
    } else {
      // Frontend without streaming: [Category]
      prefix = `[${category}]:`;
    }

    // Choose console method based on level
    const levelLower = level.toLowerCase();
    let consoleMethod: 'log' | 'warn' | 'error' | 'debug';

    switch (levelLower) {
      case 'error':
        consoleMethod = 'error';
        break;
      case 'warn':
        consoleMethod = 'warn';
        break;
      case 'debug':
      case 'trace':
        consoleMethod = 'debug';
        break;
      default:
        consoleMethod = 'log';
    }

    // Output to console
    if (args.length > 0) {
      console[consoleMethod](prefix, message, ...args);
    } else {
      console[consoleMethod](prefix, message);
    }
  }

  /**
   * Check if log level passes the filter.
   */
  private passesLevelFilter(logLevel: string): boolean {
    const levelFilter = this.filters.level.toLowerCase();
    if (levelFilter === 'all') {
      return true;
    }

    const levels = ['trace', 'debug', 'info', 'warn', 'error'];
    const logLevelIndex = levels.indexOf(logLevel.toLowerCase());
    const filterLevelIndex = levels.indexOf(levelFilter);

    // Handle invalid log levels - don't filter them out (show by default)
    if (logLevelIndex === -1 || filterLevelIndex === -1) {
      return true;
    }

    // Show logs at or above the filter level
    return logLevelIndex >= filterLevelIndex;
  }

  /**
   * Check if category passes the filter (supports wildcards).
   */
  private passesCategoryFilter(category: string): boolean {
    const filterCategories = this.filters.categories.trim();
    if (!filterCategories) {
      return true; // No filter, show all
    }

    const patterns = filterCategories.split(',').map(p => p.trim()).filter(Boolean);
    if (patterns.length === 0) {
      return true;
    }

    // Check each pattern
    for (const pattern of patterns) {
      if (this.matchesPattern(category, pattern)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a category matches a pattern (supports * wildcard).
   */
  private matchesPattern(category: string, pattern: string): boolean {
    // ReDoS protection: skip overly complex patterns
    if (pattern.length > 100 || (pattern.match(/\*/g) || []).length > 5) {
      return false;
    }

    // Convert wildcard pattern to regex
    const regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special chars
      .replace(/\*/g, '.*'); // Convert * to .*

    const regex = new RegExp(`^${regexPattern}$`, 'i'); // Case-insensitive
    return regex.test(category);
  }

  /**
   * Load filters from sessionStorage on initialization.
   */
  loadFromSession(): void {
    // Guard against non-browser environments (e.g., Node.js health server)
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const stored = sessionStorage.getItem('logging-config');
    if (stored) {
      try {
        const config = JSON.parse(stored);
        this.filters = {
          level: config.level || 'info',
          categories: config.categories || '',
          streamingEnabled: config.streamingEnabled || false
        };
      } catch (e) {
        // Ignore parse errors
      }
    }
  }
}

// Singleton instance
export const unifiedConsole = new UnifiedConsole();

// Load filters from session on module load (browser only)
if (typeof sessionStorage !== 'undefined') {
  unifiedConsole.loadFromSession();
}